import { Platform } from '@skedulo/protobuf-web'
import { UIErrorCode } from '../constants'
import { emitError } from './event-bus'

export type PlatformAPIError = Platform.Error.Error

export type GrpcError = { code?: number; message?: string }

export const isPlatformAPIError = (
  error: unknown,
): error is PlatformAPIError => {
  const maybePlatformError = error as PlatformAPIError
  const hasErrorCode = !!maybePlatformError?.code
  const hasNumericErrorCode = typeof maybePlatformError?.code === 'number'

  return hasErrorCode && !hasNumericErrorCode
}

export const isGrpcError = (error: unknown): error is GrpcError => {
  const maybeGrpcError = error as GrpcError
  const hasErrorCode = !!maybeGrpcError?.code
  const hasNumericErrorCode = typeof maybeGrpcError?.code === 'number'

  return hasErrorCode && hasNumericErrorCode
}

/**
 * A custom error object that includes an error code and traceId.
 *
 * As a general rule, you should almost always use this instead of plain Error
 * objects within platform to ensure we include useful debugging/tracing context
 * from wherever the errors get caught.
 */
export class PlatformError extends Error {
  constructor(
    message?: string,
    readonly code?: UIErrorCode | string,
    readonly traceId?: string,
    readonly metadata?: Record<string, unknown>,
  ) {
    super(message)
  }
}

/**
 * Throws a new Platform Error object. This is a special type of error that
 * includes a platform error code and traceId where relevant.
 */
export const throwPlatformError = (
  ...params: ConstructorParameters<typeof PlatformError>
) => {
  throw new PlatformError(...params)
}

const extractPlatformError = (error: unknown) => {
  if (isGrpcError(error)) {
    return new PlatformError(error?.message)
  }

  if (isPlatformAPIError(error)) {
    return new PlatformError(error.message, error.code, error.traceId)
  }

  return error
}

/**
 * Convenience error handler for try/catch blocks. Checks the shape of a given
 * error and attempts to rethrow it as a PlatformError with extra context. If
 * all else fails the original error is rethrown anyway.
 *
 * Also emits the errors to the eventbus to be logged in Datadog.
 */
export const handlePlatformError = (error: unknown) => {
  const platformError = extractPlatformError(error)
  emitError(platformError)

  throw platformError
}

/**
 * Logs an error to Datadog.
 */
export const logDataDogError = (error: Error, context?: object | undefined) => {
  if (window.DD_RUM && window.DD_RUM?.getInternalContext()) {
    window.DD_RUM.addError(error, context)
  }
}

/**
 * Logs an error to Datadog RUM if available, otherwise falls back to console.error.
 */
export const logError = (error: Error, context?: object | undefined): void => {
  if (globalThis.window?.DD_RUM?.getInternalContext?.()) {
    globalThis.window.DD_RUM.addError(error, context)
  } else {
    console.error(error, context)
  }
}

/**
 * Creates a logError function that automatically enriches Datadog context
 * with package metadata. Useful for identifying which package an error
 * originated from in Datadog RUM.
 *
 * @example
 * ```ts
 * import { createLogError } from '@skedulo/horizon-core'
 * import packageJson from '../package.json'
 *
 * export const logError = createLogError({
 *   package: packageJson.name,
 *   version: packageJson.version,
 * })
 * ```
 *
 * Without metadata, behaves identically to `logError`.
 */
export const createLogError = (metadata?: Record<string, string>) => {
  return (error: Error, context?: object | undefined): void => {
    const enrichedContext = metadata ? { ...metadata, ...context } : context
    logError(error, enrichedContext)
  }
}

const DEBUG_LOGGING_FLAG = 'SKEDULO_DEBUG_LOGGING'

/**
 * Logs a debug message to the console, gated by a localStorage flag.
 * No-op unless `localStorage.getItem('SKEDULO_DEBUG_LOGGING') === 'true'`.
 * Use for expected/informational conditions that must not reach Datadog as errors.
 */
export const logDebug = (
  message: string | Error,
  context?: Record<string, unknown>,
): void => {
  try {
    if (globalThis.localStorage?.getItem(DEBUG_LOGGING_FLAG) !== 'true') return
  } catch {
    // localStorage may throw in sandboxed iframes or private-browsing edge cases
    return
  }
  console.debug(message instanceof Error ? message.message : message, context)
}
